import { useEffect, useState, type FormEvent } from "react";
import { api, ApiError } from "../../api/client";
import type { Athlete, Coach, Group, Page, WorkoutTemplate } from "../../api/types";
import { useAuth } from "../../auth/AuthContext";
import {
  Badge,
  Button,
  EmptyState,
  ErrorState,
  FormError,
  Loading,
  Modal,
  PageHead,
  SelectField,
  TextArea,
  TextField,
  useToast,
} from "../../components/ui";
import { fmtLong } from "../../lib/training";
import { useAsync } from "../../lib/useAsync";
import { Link } from "../../router/router";
import { t, tn } from "../../i18n";

export function GroupsPage({ orgId }: { orgId: string }) {
  const { can, membership } = useAuth();
  const toast = useToast();
  const canManage = can("groups.manage");
  const canPlan = can("plans.manage");
  const [prescribing, setPrescribing] = useState<Group | null>(null);
  const [members, setMembers] = useState<Group | null>(null);
  const canMembers = (g: Group) =>
    can("groups.members") && (membership?.roles.includes("org_manager") || g.coach?.id === membership?.coach_id);
  const canPrescribe = (g: Group) =>
    canPlan && g.is_active && (membership?.roles.includes("org_manager") || g.coach?.id === membership?.coach_id);
  const showActions = canManage || canPlan || can("groups.members");
  const [showInactive, setShowInactive] = useState(false);
  const list = useAsync(() => api.get<Group[]>(`/orgs/${orgId}/groups`, { include_inactive: showInactive }), [orgId, showInactive]);
  const coaches = useAsync(() => (canManage ? api.get<Coach[]>(`/orgs/${orgId}/coaches`) : Promise.resolve([])), [orgId, canManage]);
  const [editing, setEditing] = useState<Group | "new" | null>(null);

  async function toggle(g: Group) {
    try {
      await api.patch(`/orgs/${orgId}/groups/${g.id}`, { is_active: !g.is_active });
      toast(g.is_active ? t("Grupo {name} arquivado.", { name: g.name }) : t("Grupo {name} reativado.", { name: g.name }));
      list.reload();
    } catch (err) {
      toast((err as ApiError).message, "error");
    }
  }

  return (
    <>
      <PageHead
        title={t("Grupos")}
        subtitle={t("Turmas de treino. O treinador coordenador de um grupo acompanha todos os atletas dele.")}
        actions={canManage && <Button variant="primary" onClick={() => setEditing("new")}>{t("Criar grupo")}</Button>}
      />
      {canManage && (
        <label className="checkbox" style={{ marginBottom: 12 }}>
          <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
          {t("Mostrar arquivados")}
        </label>
      )}
      {list.loading && !list.data && <Loading />}
      {list.error && <ErrorState error={list.error} onRetry={list.reload} />}
      {list.data && list.data.length === 0 && (
        <div className="panel">
          <EmptyState
            title={canManage ? t("Nenhum grupo criado") : t("Você não coordena nenhum grupo")}
            action={canManage && <Button variant="primary" onClick={() => setEditing("new")}>{t("Criar grupo")}</Button>}
          >
            {canManage ? t("Use grupos para organizar atletas por objetivo, nível ou local de treino.") : undefined}
          </EmptyState>
        </div>
      )}
      {list.data && list.data.length > 0 && (
        <div className="table-wrap">
          <table className="data responsive">
            <thead>
              <tr>
                <th>{t("Grupo")}</th>
                <th>{t("Coordenação")}</th>
                <th className="right">{t("Atletas")}</th>
                {showActions && <th className="right">{t("Ações")}</th>}
              </tr>
            </thead>
            <tbody>
              {list.data.map((g) => (
                <tr key={g.id}>
                  <td>
                    <strong>{g.name}</strong> {!g.is_active && <Badge>{t("Arquivado")}</Badge>}
                    {g.description && <span className="sub">{g.description}</span>}
                  </td>
                  <td data-label={t("Coordenação")}>{g.coach?.name ?? <span className="muted">{t("Sem coordenador")}</span>}</td>
                  <td data-label={t("Atletas")} className="right num">
                    {g.athletes_count > 0 && can("athletes.read") ? (
                      <Link to={`/org/${orgId}/atletas?grupo=${g.id}`}>{g.athletes_count}</Link>
                    ) : (
                      g.athletes_count
                    )}
                  </td>
                  {showActions && (
                    <td className="right" style={{ whiteSpace: "nowrap" }}>
                      {canMembers(g) && g.is_active && (
                        <Button size="sm" variant="secondary" onClick={() => setMembers(g)}>
                          {t("Atletas")}
                        </Button>
                      )}
                      {canPrescribe(g) && g.athletes_count > 0 && (
                        <Button size="sm" variant="secondary" onClick={() => setPrescribing(g)}>
                          {t("Prescrever treino")}
                        </Button>
                      )}
                      {canManage && (
                        <>
                          <Button size="sm" variant="ghost" onClick={() => setEditing(g)}>
                            {t("Editar")}
                          </Button>
                          <Button size="sm" variant={g.is_active ? "danger" : "secondary"} onClick={() => toggle(g)}>
                            {g.is_active ? t("Arquivar") : t("Reativar")}
                          </Button>
                        </>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <PrescribeModal orgId={orgId} group={prescribing} onClose={() => setPrescribing(null)} />
      <MembersModal
        orgId={orgId}
        group={members}
        onClose={() => setMembers(null)}
        onSaved={() => {
          setMembers(null);
          list.reload();
        }}
      />
      {canManage && (
        <GroupModal
          orgId={orgId}
          group={editing}
          coaches={coaches.data ?? []}
          onClose={() => setEditing(null)}
          onSaved={(msg) => {
            setEditing(null);
            toast(msg);
            list.reload();
          }}
        />
      )}
    </>
  );
}

function GroupModal({
  orgId,
  group,
  coaches,
  onClose,
  onSaved,
}: {
  orgId: string;
  group: Group | "new" | null;
  coaches: Coach[];
  onClose: () => void;
  onSaved: (msg: string) => void;
}) {
  const isNew = group === "new";
  const [form, setForm] = useState({ name: "", description: "", coach_id: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  useEffect(() => {
    setError(null);
    if (group && group !== "new") setForm({ name: group.name, description: group.description ?? "", coach_id: group.coach?.id ?? "" });
    else setForm({ name: "", description: "", coach_id: "" });
  }, [group]);
  const set = (k: keyof typeof form) => (e: { target: { value: string } }) => setForm({ ...form, [k]: e.target.value });

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const body = { name: form.name, description: form.description || null, coach_id: form.coach_id || null };
    try {
      if (isNew) {
        await api.post(`/orgs/${orgId}/groups`, body);
        onSaved(t("Grupo criado."));
      } else if (group) {
        await api.patch(`/orgs/${orgId}/groups/${group.id}`, body);
        onSaved(t("Grupo atualizado."));
      }
    } catch (err) {
      setError(err as ApiError);
    } finally {
      setBusy(false);
    }
  }
  const f = error?.fieldErrors() ?? {};
  return (
    <Modal
      open={group !== null}
      title={isNew ? t("Criar grupo") : t("Editar grupo")}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("Cancelar")}
          </Button>
          <Button variant="primary" type="submit" form="group-form" busy={busy}>
            {isNew ? t("Criar grupo") : t("Salvar alterações")}
          </Button>
        </>
      }
    >
      <form id="group-form" className="form" onSubmit={submit} noValidate>
        {error && error.code !== "validation_error" && <FormError error={error} />}
        <TextField label={t("Nome")} value={form.name} onChange={set("name")} error={f.name} autoFocus />
        <SelectField label={t("Treinador coordenador")} value={form.coach_id} onChange={set("coach_id")} error={f.coach_id}>
          <option value="">{t("Sem coordenador")}</option>
          {coaches.map((c) => (
            <option key={c.id} value={c.id}>
              {c.full_name}
            </option>
          ))}
        </SelectField>
        <TextArea label={t("Descrição")} value={form.description} onChange={set("description")} />
      </form>
    </Modal>
  );
}

/* ------------------------------------------------------------------ prescrição para o grupo */

function PrescribeModal({ orgId, group, onClose }: { orgId: string; group: Group | null; onClose: () => void }) {
  const toast = useToast();
  const templates = useAsync(
    () => (group ? api.get<WorkoutTemplate[]>(`/orgs/${orgId}/workout-templates`) : Promise.resolve([])),
    [orgId, group?.id],
  );
  const [templateId, setTemplateId] = useState("");
  const [dateInput, setDateInput] = useState("");
  const [dates, setDates] = useState<string[]>([]);
  const [time, setTime] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [result, setResult] = useState<{ athletes: number; sessions_created: number } | null>(null);

  useEffect(() => {
    setTemplateId("");
    setDates([]);
    setDateInput("");
    setTime("");
    setError(null);
    setResult(null);
  }, [group]);

  function addDate() {
    if (dateInput && !dates.includes(dateInput)) setDates([...dates, dateInput].sort());
    setDateInput("");
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!group) return;
    setBusy(true);
    setError(null);
    try {
      setResult(
        await api.post(`/orgs/${orgId}/groups/${group.id}/prescribe`, {
          template_id: templateId,
          dates,
          start_time: time || null,
        }),
      );
    } catch (err) {
      setError(err as ApiError);
    } finally {
      setBusy(false);
    }
  }

  async function publishAll() {
    if (!group) return;
    setBusy(true);
    try {
      const r = await api.post<{ plans_published: number }>(`/orgs/${orgId}/groups/${group.id}/publish`, {});
      toast(tn("{n} planilha publicada para o grupo {name}.", "{n} planilhas publicadas para o grupo {name}.", r.plans_published, { name: group.name }));
      onClose();
    } catch (err) {
      toast((err as ApiError).message, "error");
    } finally {
      setBusy(false);
    }
  }

  const selected = templates.data?.find((tp) => tp.id === templateId);
  return (
    <Modal open={group !== null} title={group ? t("Prescrever para {name}", { name: group.name }) : ""} onClose={onClose}
      footer={
        result ? (
          <>
            <Button variant="ghost" onClick={onClose}>
              {t("Ajustar antes de publicar")}
            </Button>
            <Button variant="primary" onClick={publishAll} busy={busy}>
              {t("Publicar para o grupo")}
            </Button>
          </>
        ) : (
          <>
            <Button variant="ghost" onClick={onClose}>
              {t("Cancelar")}
            </Button>
            <Button variant="primary" type="submit" form="prescribe" busy={busy} disabled={!templateId || dates.length === 0}>
              {t("Adicionar às planilhas")}
            </Button>
          </>
        )
      }>
      {result ? (
        <div className="stack">
          <div className="alert alert-ok" role="status">
            <p>
              {t("Treinos adicionados ao rascunho dos atletas ativos do grupo: {sessions}, para {athletes}.", { sessions: tn("{n} treino", "{n} treinos", result.sessions_created), athletes: tn("{n} atleta", "{n} atletas", result.athletes) })}</p>
          </div>
          <p>
            {t("Você pode publicar agora para todos ou abrir a planilha de cada atleta para ajustes individuais (ritmos, distância) e publicar depois.")}
          </p>
        </div>
      ) : (
        <form id="prescribe" className="form" onSubmit={submit} noValidate>
          {error && <FormError error={error} />}
          {templates.data && templates.data.length === 0 ? (
            <EmptyState title={t("Nenhum modelo cadastrado")} action={<Link to={`/org/${orgId}/modelos`}>{t("Criar modelos de treino")}</Link>}>
              {t("A prescrição para grupos usa modelos de treino.")}
            </EmptyState>
          ) : (
            <>
              <SelectField label={t("Modelo de treino")} value={templateId} onChange={(e) => setTemplateId(e.target.value)}
                hint={selected?.totals.summary_text || undefined}>
                <option value="">{t("Escolha um modelo")}</option>
                {templates.data?.map((tp) => (
                  <option key={tp.id} value={tp.id}>
                    {tp.name}
                  </option>
                ))}
              </SelectField>
              <div className="field">
                <label htmlFor="prescribe-date">{t("Datas")}</label>
                <div className="input-group">
                  <input id="prescribe-date" className="input" type="date" value={dateInput} onChange={(e) => setDateInput(e.target.value)} />
                  <Button onClick={addDate} disabled={!dateInput}>
                    {t("Adicionar data")}
                  </Button>
                </div>
                {dates.length > 0 && (
                  <ul className="chips" aria-label={t("Datas escolhidas")}>
                    {dates.map((d) => (
                      <li key={d}>
                        {fmtLong(d)}
                        <button type="button" aria-label={t("Remover {date}", { date: fmtLong(d) })} onClick={() => setDates(dates.filter((x) => x !== d))}>
                          ×
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <TextField label={t("Horário (opcional)")} type="time" value={time} onChange={(e) => setTime(e.target.value)} />
              <p className="muted">
                {t("O treino entra no rascunho da planilha de cada atleta ativo do grupo ({n} no total, pausados não recebem). Nada é enviado aos atletas antes da publicação.", { n: group?.athletes_count ?? 0 })}
              </p>
            </>
          )}
        </form>
      )}
    </Modal>
  );
}


function MembersModal({
  orgId,
  group,
  onClose,
  onSaved,
}: {
  orgId: string;
  group: Group | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const athletes = useAsync(
    () =>
      group
        ? api.get<Page<Athlete>>(`/orgs/${orgId}/athletes`, { status: "active", page_size: 100, sort: "name" })
        : Promise.resolve(null),
    [orgId, group?.id],
  );
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const initial = new Set((athletes.data?.items ?? []).filter((a) => a.group?.id === group?.id).map((a) => a.id));
  useEffect(() => {
    setSelected(new Set((athletes.data?.items ?? []).filter((a) => a.group?.id === group?.id).map((a) => a.id)));
    setQ("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [athletes.data, group?.id]);

  const items = (athletes.data?.items ?? []).filter((a) => a.full_name.toLowerCase().includes(q.trim().toLowerCase()));
  const toggle = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };
  const add = [...selected].filter((id) => !initial.has(id));
  const remove = [...initial].filter((id) => !selected.has(id));

  async function save() {
    if (!group) return;
    setBusy(true);
    try {
      await api.post(`/orgs/${orgId}/groups/${group.id}/members`, { add, remove });
      toast(t("Grupo {name} atualizado.", { name: group.name }));
      onSaved();
    } catch (err) {
      toast((err as ApiError).message, "error");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open={group !== null}
      title={group ? t("Atletas de {name}", { name: group.name }) : ""}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("Cancelar")}
          </Button>
          <Button variant="primary" onClick={save} busy={busy} disabled={add.length + remove.length === 0}>
            {add.length + remove.length > 0 ? t("Salvar ({add} entram, {remove} saem)", { add: add.length, remove: remove.length }) : t("Salvar")}
          </Button>
        </>
      }
    >
      {athletes.loading && <Loading />}
      {athletes.error && <ErrorState error={athletes.error} onRetry={athletes.reload} />}
      {athletes.data && (
        <>
          <TextField label={t("Buscar atleta")} value={q} onChange={(e) => setQ(e.target.value)} />
          {items.length === 0 && <p className="muted">{t("Nenhum atleta ativo disponível para você.")}</p>}
          <ul className="member-list">
            {items.map((a) => (
              <li key={a.id}>
                <label className="checkbox">
                  <input type="checkbox" checked={selected.has(a.id)} onChange={() => toggle(a.id)} />
                  <span>{a.full_name}</span>
                </label>
                {a.group && a.group.id !== group?.id && <span className="muted"> {t("· hoje em {name}", { name: a.group.name })}</span>}
              </li>
            ))}
          </ul>
        </>
      )}
    </Modal>
  );
}
